import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import * as CANNON from "cannon-es";
import { Lights } from "./lights";
import { getValueFromDice } from "./map-dice-normal";

// Code un peu a l'arache car pas le time :(
// I used mesh box geometry to use with raycaster to get normal face with unit value like 1 or -1

/**
 * Base
 */
// Canvas
const canvas = document.querySelector("canvas.webgl");

const panelEl = document.getElementById("panel");
const divElement = document.getElementById("value");
const resetEl = document.getElementById("reroll");

// Scene
const scene = new THREE.Scene();

/**
 * Physics
 */
const world = new CANNON.World();
world.broadphase = new CANNON.SAPBroadphase(world);
world.allowSleep = true;
world.gravity.set(0, -9.82, 0);

// Default material
const defaultMaterial = new CANNON.Material("default");
const defaultContactMaterial = new CANNON.ContactMaterial(
  defaultMaterial,
  defaultMaterial,
  {
    friction: 0.5,
    restitution: 0.5,
  }
);
// Apply the same contact material for all body
world.defaultContactMaterial = defaultContactMaterial;

/**
 * Floor
 */
const floorShape = new CANNON.Plane();
const floorBody = new CANNON.Body();
floorBody.mass = 0;
floorBody.addShape(floorShape);
// By default floor is not on the correct direction
floorBody.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI * 0.5);
world.addBody(floorBody);

const floor = new THREE.Mesh(
  new THREE.PlaneGeometry(20, 20),
  new THREE.MeshStandardMaterial({
    color: "#000000",
  })
);
floor.receiveShadow = true;
floor.rotation.x = -Math.PI * 0.5;
scene.add(floor);

/**
 * Utils
 */
const objectsToUpdate = [];

// Create box
const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
const boxMaterial = new THREE.MeshStandardMaterial({
  metalness: 0.3,
  roughness: 0.4,
});

const createBox = (width, height, depth, position, diceModel) => {
  // Three.js mesh
  const mesh = new THREE.Mesh(boxGeometry, boxMaterial);
  mesh.castShadow = true;
  // To avoid diceModel size less than mesh
  mesh.scale.set(width * 0.8, height * 0.8, depth * 0.8);
  mesh.position.copy(position);
  scene.add(mesh);

  // Metadata
  const metadata = {
    isSleeping: false,
  };

  // Cannon.js body
  const shape = new CANNON.Box(
    // Cannon use value from center to create box so we need to set up the size from center (it's why is 0.5)
    new CANNON.Vec3(width * 0.5, height * 0.5, depth * 0.5)
  );

  const body = new CANNON.Body({
    mass: 1,
    position,
    shape: shape,
    material: defaultMaterial,
  });
  // Set first rotation
  // We use random value to have more randomness
  const randomXAxisDirection = Math.random() < 0.5 ? 1 : -1;
  const randomYAxisDirection = Math.random() < 0.5 ? 1 : -1;
  const randomZAxisDirection = Math.random() < 0.5 ? 1 : -1;
  const randomAngleRotation = (Math.random() < 0.5 ? 1 : -1) * (Math.PI / 6);
  body.quaternion.setFromAxisAngle(
    new CANNON.Vec3(
      randomXAxisDirection,
      randomYAxisDirection,
      randomZAxisDirection
    ),
    randomAngleRotation
  );
  body.addEventListener("sleep", (e) => {
    metadata.isSleeping = true;
  });
  body.addEventListener("wakeup", (e) => {
    metadata.isSleeping = false;
  });
  world.addBody(body);

  // Save in objects
  objectsToUpdate.push({ mesh, body, metadata, diceModel });
};

/**
 * Lights
 */
scene.add(Lights.ambientLight);
scene.add(Lights.directionalLight);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.set(-3, 3, 3);
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
// Save state to use this when reset scene
controls.saveState();

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();
let oldElapsedTime = 0;

const castRaycaster = (object) => {
  const raycaster = new THREE.Raycaster();
  const rayDirection = new THREE.Vector3(0, -10, 0);
  rayDirection.normalize();

  const rayOrigin = new THREE.Vector3(
    object.position.x,
    object.position.y + 5,
    object.position.z
  );

  raycaster.set(rayOrigin, rayDirection);
  return raycaster.intersectObject(object);
};

const objectsToUpdateHandler = (objects) => {
  const firstObject = objects[0];
  let isAllSleeping = !!firstObject.metadata.isSleeping;
  const watch = () => {
    isAllSleeping = !!firstObject.metadata.isSleeping;

    const watchAllSleeping = (isSleeping) => {
      // We check if ALL object are sleeping
      isAllSleeping === false
        ? (isAllSleeping = false)
        : (isAllSleeping = isSleeping);
    };
    // Apply body effect to mesh
    for (const object of objects) {
      object.mesh.position.copy(object.body.position);
      object.mesh.quaternion.copy(object.body.quaternion);
      object.diceModel.position.copy(object.body.position);
      object.diceModel.quaternion.copy(object.body.quaternion);
      watchAllSleeping(object.metadata.isSleeping);
    }
  };

  let isAllSleepingEvent = () => {
    console.log("finish");
    const resultNormals = [];
    for (const object of objects) {
      const intersectObject = castRaycaster(object.mesh)[0];

      const { x, y, z } = intersectObject.face.normal;
      const normalDice = getValueFromDice({ x, y, z });
      resultNormals.push(normalDice);
    }

    const showAndUpdateDivElement = () => {
      const dice1Value = resultNormals[0];
      const dice2Value = resultNormals[1];

      const getResult = dice1Value + dice2Value;
      const dice1String = String(dice1Value);
      const dice2String = String(dice2Value);

      const isDouble = dice1Value === dice2Value;

      const innerText = isDouble
        ? `DOUBLE !!! ${dice1String} + ${dice2String} = ${getResult}`
        : `${dice1String} + ${dice2String} = ${getResult}`;

      divElement.innerText = innerText;
      panelEl.style.display = "block";
    };

    showAndUpdateDivElement();

    // trigger function only one time
    isAllSleepingEvent = () => {};
  };

  let trigger = () => {
    if (isAllSleeping) {
      isAllSleepingEvent();
      return;
    }

    watch();
  };

  return trigger;
};

let watchObjects = () => {};

const resetRoll = () => {
  for (const object of objectsToUpdate) {
    const { diceModel, body, mesh } = object;
    // Dispose dice
    scene.remove(diceModel);

    // Remove body
    world.removeBody(body);

    // Remove mesh
    scene.remove(mesh);
  }

  objectsToUpdate.splice(0, objectsToUpdate.length);
  divElement.innerText = "";
  panelEl.style.display = "none";
};

const gltfLoader = new GLTFLoader();

const createDice = async (position) => {
  const gltfLoaderScene = await gltfLoader.loadAsync(
    "./models/dice/scene.gltf"
  );
  const dice = gltfLoaderScene.scene.children[0].children[0].children[0];
  // Fix position when load model
  dice.position.set(position);
  dice.scale.set(0.026, 0.026, 0.026);
  scene.add(dice);

  return dice;
};

const initRoll = async () => {
  const position1 = { x: 0, y: 5, z: 0 };
  const position2 = { x: 0, y: 6, z: 0 };

  const dice1 = await createDice(position1);
  const dice2 = await createDice(position2);
  createBox(0.5, 0.5, 0.5, position1, dice1);
  createBox(0.5, 0.5, 0.5, position2, dice2);

  watchObjects = objectsToUpdateHandler(objectsToUpdate);
};

await initRoll();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();
  const deltaTime = elapsedTime - oldElapsedTime;
  oldElapsedTime = elapsedTime;

  // Update physics
  world.step(1 / 60, deltaTime, 3);

  watchObjects();

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();

resetEl.addEventListener("click", async () => {
  resetRoll();
  await initRoll();
  // reset camera from save state
  controls.reset();
});
